import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import AddIcon from "@mui/icons-material/Add";
import * as React from "react";
import {useTranslation} from "react-i18next";
import {Box, FormControlLabel, Stack, Switch} from "@mui/material";
import {UseEntityData} from "crqlar-react-common/dist/components/useEntity";
import {Reservation} from "crqlar-react-common/dist/models/reservation/reservation.model";
import Property from "crqlar-react-common/dist/components/Property";
import Typography from "@mui/material/Typography";
import {NoteGroupIcon} from "crqlar-react-common/dist/components/note/NoteGroupIcon";
import {useNoteGroupIndex} from "crqlar-react-common/dist/utils/hooks/notes/useNoteGroupIndex";

interface Props {
    data: UseEntityData<Reservation>
    disabled: boolean
}

const InternalNotes = (props: Props) => {

    const {t} = useTranslation()
    const noteGroupIndex = useNoteGroupIndex()
    const reservation = props.data.item
    const setReservation = props.data.setItem
    const updateField = (field: keyof Reservation, value: any) => {
        setReservation({...reservation, [field]: value})
    }

    const deleteNote = (index: number) => {
        const internalNotes = [...(reservation.notes || [])]
        internalNotes.splice(index, 1)
        updateField('notes', internalNotes)
    }

    return (
        <Stack gap={{xs: 3, sm: 2.5}}>
            {
                reservation.notes?.map((internalNote, index) => (
                    <Stack
                        key={index}
                        sx={{
                            border: '1px solid',
                            borderColor: 'divider',
                            p: {xs: 1.5, sm: 2},
                            gap: 1.5,
                        }}
                    >
                        <Stack
                            direction={'row'}
                            justifyContent={'space-between'}
                            alignItems={'center'}
                            gap={1}
                        >
                            <Typography variant={'subtitle2'} fontWeight={600}>
                                {t('Note')} {index + 1}
                            </Typography>

                            {
                                !props.disabled &&
                                <Button
                                    variant={'text'}
                                    size={'small'}
                                    color={'error'}
                                    onClick={() => deleteNote(index)}
                                >
                                    {t('Delete')}
                                </Button>
                            }
                        </Stack>

                        <Property title={t('Note')} labelAlignment={'top'}>
                            <TextField
                                id={`internal-note-${index}`}
                                size={'small'}
                                value={internalNote.note}
                                onChange={(e) => {
                                    const internalNotes = [...(reservation.notes || [])]
                                    internalNotes[index].note = e.target.value
                                    updateField('notes', internalNotes)
                                }}
                                fullWidth
                                multiline
                                rows={3}
                                disabled={props.disabled}
                                InputProps={{
                                    startAdornment: (
                                        <NoteGroupIcon
                                            note={internalNote}
                                            index={noteGroupIndex}
                                            sx={{mr: 1, alignSelf: 'flex-start', mt: '3px'}}
                                        />
                                    ),
                                }}
                            />
                        </Property>

                        <Property title={t('Note type')} labelAlignment={'top'}>
                            <TextField
                                id={`internal-note-${index}-type`}
                                size={'small'}
                                value={internalNote.name || ''}
                                onChange={(e) => {
                                    const internalNotes = [...(reservation.notes || [])]
                                    internalNotes[index] = {
                                        ...internalNotes[index],
                                        name: e.target.value
                                    }
                                    updateField('notes', internalNotes)
                                }}
                                fullWidth
                                disabled={props.disabled}
                            />
                        </Property>

                        <Box sx={{pl: {sm: '200px'}}}>
                            <FormControlLabel
                                control={
                                    <Switch
                                        id={`internal-note-${index}-public-switch`}
                                        checked={internalNote.guestViewable}
                                        onChange={(e) => {
                                            const internalNotes = [...(reservation.notes || [])]
                                            internalNotes[index].guestViewable = e.target.checked
                                            updateField('notes', internalNotes)
                                        }}
                                        disabled={props.disabled}
                                    />
                                }
                                label={t('Visible to guest')}
                            />
                        </Box>

                    </Stack>
                ))
            }

            {
                !props.disabled &&
                <Button
                    id={'add-note'}
                    startIcon={<AddIcon/>}
                    onClick={() => {
                        const internalNotes = [...(reservation.notes || [])]
                        internalNotes.push({note: '', guestViewable: false})
                        updateField('notes', internalNotes)
                    }}
                    sx={{
                        alignSelf: 'flex-start'
                    }}
                >
                    {t('Add note')}
                </Button>
            }

        </Stack>
    )
}

export default InternalNotes;
